import type { SourceKind, SourceUse } from '../../../shared/protocol.ts';
import type { VideoPreset } from './capability.ts';
import { bestLevelFor, LEVELS, liveLoadFor, planQuality, type QualityMode, type QualityPlan, type VideoKind } from './quality.ts';
import { Observable, uid } from './emitter.ts';
import { pageProcessorInstalled } from './track-processor.ts';

/**
 * Everything this participant is capturing. Each source says whether it is part of
 * "me" on the call (live), saved locally at full quality (record), or both.
 */
export interface CapturedSource {
  id: string;
  kind: SourceKind;
  label: string;
  deviceId?: string;
  track: MediaStreamTrack;
  /** A screen share's sound (tab or system audio), sent live with the picture. It is recorded separately, as the
   * screen's "sound" source (see parentId). */
  audioTrack?: MediaStreamTrack;
  /** For a screen's sound: the screen it belongs to. It ends when the screen does. */
  parentId?: string;
  stream: MediaStream;
  use: SourceUse;
  /** "Mute sending": not sent to the call, still recorded (marked on the timeline). */
  liveMuted: boolean;
  /** "Hard mute": not sent and not recorded – silence or black frames are written so the file stays in sync. */
  hardMuted: boolean;
  ended: boolean;
  addedAt: number;
  /** Cameras and screens: the best level (quality.ts) the device can deliver. */
  bestLevel?: number;
  /** The level the track was last set to. */
  appliedLevel?: number;
}

export type AudioProcessing = 'voice' | 'studio';

const PREFS_KEY = 'takehouse.devices.v1';

interface DevicePrefs {
  camera?: string;
  microphone?: string;
  processing?: AudioProcessing;
  quality?: QualityMode;
}

export function loadPrefs(): DevicePrefs {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function savePrefs(p: DevicePrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...p }));
  } catch {}
}

export class SourceManager extends Observable {
  sources: CapturedSource[] = [];
  devices: MediaDeviceInfo[] = [];
  processing: AudioProcessing = loadPrefs().processing ?? 'voice';
  /** Recording quality: see quality.ts. */
  mode: QualityMode = loadPrefs().quality ?? 'smart';
  /** This computer's budget, in 720p 30 fps streams (set once the hardware check has run). */
  capacity = 3;
  /** People receiving this person's live video (1 before joining: the test assumes a one-to-one call). */
  private liveReceivers = 1;
  /** What the last 6-second test showed about the plan it ran with. */
  tested: { result: 'clean' | 'struggled'; load: number; at: number } | null = null;
  /** Levels set by hand, by source. */
  private chosen = new Map<string, number>();
  /** Levels of sources being recorded now, which stay put until their file ends. */
  private recording = new Map<string, number>();
  error: string | null = null;
  /** After a screen share that came without sound: why, and how to get it. */
  soundNote: string | null = null;
  private onEnded = new Set<(s: CapturedSource) => void>();

  constructor() {
    super();
    navigator.mediaDevices?.addEventListener('devicechange', () => void this.refreshDevices());
  }

  onSourceEnded(fn: (s: CapturedSource) => void) {
    this.onEnded.add(fn);
    return () => this.onEnded.delete(fn);
  }

  async refreshDevices() {
    try {
      this.devices = await navigator.mediaDevices.enumerateDevices();
      this.changed();
    } catch {}
  }

  get cameras() {
    return this.devices.filter((d) => d.kind === 'videoinput' && d.deviceId);
  }
  get microphones() {
    return this.devices.filter((d) => d.kind === 'audioinput' && d.deviceId && d.deviceId !== 'communications');
  }

  get active() {
    return this.sources.filter((s) => !s.ended);
  }

  byId(id: string) {
    return this.sources.find((s) => s.id === id);
  }

  /** The camera other people see as "me". */
  get primaryCamera() {
    return this.active.find((s) => s.kind === 'camera' && s.use.live);
  }
  get primaryMic() {
    return this.active.find((s) => s.kind === 'microphone' && s.use.live);
  }

  /** Cameras and screens that are recorded, as the quality plan sees them. */
  private planSources() {
    const firstScreen = this.active.find((s) => s.kind === 'screen' && s.use.record);
    return this.active
      .filter((s): s is CapturedSource & { kind: VideoKind } => (s.kind === 'camera' || s.kind === 'screen') && s.use.record)
      .map((s) => ({
        id: s.id,
        kind: s.kind,
        main: s.kind === 'camera' ? s.use.live : s === firstScreen,
        best: s.bestLevel ?? 3,
        chosen: this.chosen.get(s.id),
        locked: this.recording.get(s.id),
      }));
  }

  get plan(): QualityPlan {
    const liveVideos = this.active.filter((s) => (s.kind === 'camera' || s.kind === 'screen') && s.use.live && !s.liveMuted).length;
    return planQuality(this.mode, this.planSources(), this.capacity, liveLoadFor(liveVideos, this.liveReceivers));
  }

  /** The call grew or shrank: the live share of the encoder changes, so the plan may too. */
  setLiveReceivers(n: number) {
    n = Math.max(1, n);
    if (n === this.liveReceivers) return;
    this.liveReceivers = n;
    void this.applyPlan();
  }

  /**
   * Corrects the budget from a 6-second test of the current plan. Struggling (frames queueing for the encoder)
   * lowers it below what was tried, so Smart steps down; recording cleanly raises it to at least what was tried.
   */
  learnFromTest(struggled: boolean) {
    const plan = this.plan;
    const used = plan.load + plan.liveLoad;
    this.capacity = struggled ? Math.min(this.capacity, used * 0.8) : Math.max(this.capacity, used);
    this.tested = { result: struggled ? 'struggled' : 'clean', load: plan.load, at: Date.now() };
    console.info(`[takehouse] 6-second test ${struggled ? 'struggled' : 'ran cleanly'} at ${used.toFixed(1)} units; budget now ${this.capacity.toFixed(1)}`);
    void this.applyPlan();
  }

  /** What a camera or screen is recorded at, from the plan. */
  videoFor(source: CapturedSource): VideoPreset | undefined {
    if (source.kind !== 'camera' && source.kind !== 'screen') return undefined;
    const p = this.plan.sources.get(source.id);
    const level = p?.level ?? LEVELS[source.kind][3];
    return { ...level, bitrate: p?.bitrate ?? 6_000_000 };
  }

  setMode(mode: QualityMode) {
    this.mode = mode;
    savePrefs({ quality: mode });
    console.info(`[takehouse] recording quality: ${mode}`);
    this.chosen.clear();
    void this.applyPlan();
  }

  /** Sets one source's level by hand (null: back to automatic). */
  choose(id: string, level: number | null) {
    if (level == null) this.chosen.delete(id);
    else this.chosen.set(id, level);
    void this.applyPlan();
  }

  /** A recorder started on this source: its level stays as it is until release(). */
  hold(id: string) {
    const p = this.plan.sources.get(id);
    if (p) this.recording.set(id, p.index);
  }
  release(id: string) {
    if (this.recording.delete(id)) void this.applyPlan();
  }

  /**
   * Asks each camera and screen for its planned size and frame rate: a recording is never larger than what the
   * device delivers, so the track itself has to be set. Sources being recorded are left alone.
   */
  async applyPlan() {
    const plan = this.plan;
    await Promise.all(
      this.active.map(async (s) => {
        const p = plan.sources.get(s.id);
        if (!p || p.how === 'recording' || s.appliedLevel === p.index) return;
        const { width, height, fps } = p.level;
        const constraints: MediaTrackConstraints =
          s.kind === 'camera'
            ? { ...s.track.getConstraints(), width: { ideal: width }, height: { ideal: height }, frameRate: { ideal: fps } }
            : { width: { max: width }, height: { max: height }, frameRate: { ideal: fps, max: fps } };
        try {
          await s.track.applyConstraints(constraints);
          s.appliedLevel = p.index;
          const st = s.track.getSettings();
          console.info(`[takehouse] ${s.label} set to ${width}×${height} ${fps} fps (${this.mode}); delivering ${st.width}×${st.height} ${Math.round(st.frameRate ?? 0)} fps`);
        } catch (e) {
          console.warn(`[takehouse] ${s.label} couldn't change size: ${(e as Error).message}`);
        }
      }),
    );
    this.changed();
  }

  /** What to ask a new camera or screen for before the plan has seen it. */
  private openingLevel(kind: VideoKind) {
    return LEVELS[kind][this.mode === 'maximum' ? 0 : this.mode === 'smart' ? 3 : 4];
  }

  private add(s: Omit<CapturedSource, 'id' | 'ended' | 'addedAt' | 'liveMuted' | 'hardMuted' | 'stream'>) {
    const tracks = [s.track, ...(s.audioTrack ? [s.audioTrack] : [])];
    const source: CapturedSource = {
      ...s,
      id: uid(),
      stream: new MediaStream(tracks),
      liveMuted: false,
      hardMuted: false,
      ended: false,
      addedAt: Date.now(),
    };
    s.track.addEventListener('ended', () => {
      console.warn(`[takehouse] ${source.label} stopped by the browser or system (device unplugged, or taken by another tab or app)`);
      this.markEnded(source);
    });
    const st = s.track.getSettings();
    console.info(
      `[takehouse] source added: ${source.label} (${s.track.label || s.kind})${st.width ? ` ${st.width}×${st.height} ${Math.round(st.frameRate ?? 0)} fps` : ''}${st.sampleRate ? ` ${st.sampleRate} Hz ${st.channelCount ?? '?'} ch` : ''}`,
    );
    if (s.kind === 'camera' || s.kind === 'screen') source.bestLevel = bestLevelFor(s.kind, s.track);
    this.sources = [...this.sources, source];
    this.error = null;
    this.changed();
    void this.refreshDevices();
    void this.applyPlan();
    return source;
  }

  private markEnded(source: CapturedSource) {
    if (source.ended) return;
    source.ended = true;
    source.audioTrack?.stop();
    this.sources = this.sources.filter((s) => s !== source);
    this.chosen.delete(source.id);
    this.changed();
    for (const fn of this.onEnded) fn(source);
    for (const child of this.active.filter((s) => s.parentId === source.id)) this.markEnded(child);
    // Whatever it was using is free for the others.
    if (source.kind === 'camera' || source.kind === 'screen') void this.applyPlan();
  }

  async addCamera(deviceId?: string, use: SourceUse = { live: true, record: true }) {
    const p = this.openingLevel('camera');
    const stream = await this.capture('camera', () =>
      navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          width: { ideal: p.width },
          height: { ideal: p.height },
          frameRate: { ideal: p.fps },
        },
      }),
    );
    if (!stream) return null;
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    savePrefs({ camera: settings.deviceId });
    const n = this.active.filter((s) => s.kind === 'camera').length;
    return this.add({
      kind: 'camera',
      label: n ? `Camera ${n + 1}` : 'Camera',
      deviceId: settings.deviceId,
      track,
      use: n ? { live: false, record: true } : use,
    });
  }

  async addMicrophone(deviceId?: string, use: SourceUse = { live: true, record: true }) {
    const voice = this.processing === 'voice';
    const stream = await this.capture('microphone', () =>
      navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: deviceId ? { exact: deviceId } : undefined,
          echoCancellation: voice,
          noiseSuppression: voice,
          autoGainControl: voice,
          sampleRate: { ideal: 48000 },
          channelCount: { ideal: 1 },
        },
      }),
    );
    if (!stream) return null;
    const track = stream.getAudioTracks()[0];
    savePrefs({ microphone: track.getSettings().deviceId });
    const n = this.active.filter((s) => s.kind === 'microphone').length;
    return this.add({
      kind: 'microphone',
      label: n ? `Microphone ${n + 1}` : 'Microphone',
      deviceId: track.getSettings().deviceId,
      track,
      use: n ? { live: false, record: true } : use,
    });
  }

  /** Opens the browser's picker: whole screen, a single app window, or a browser tab. */
  async addScreen(use: SourceUse) {
    const p = this.openingLevel('screen');
    const stream = await this.capture('screen', () =>
      navigator.mediaDevices.getDisplayMedia({
        // Chrome opens its picker on Entire Screen, the choice that can share the computer's sound.
        video: { width: { max: p.width }, height: { max: p.height }, frameRate: { ideal: p.fps, max: p.fps }, displaySurface: 'monitor' } as MediaTrackConstraints,
        // Record the sound as it is (no voice processing), and leave this page's own output (the other people
        // on the call) out of it where the browser supports that.
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, restrictOwnAudio: true, suppressLocalAudioPlayback: false } as MediaTrackConstraints,
        // Chrome hints; ignored elsewhere.
        selfBrowserSurface: 'exclude',
        surfaceSwitching: 'include',
        systemAudio: 'include',
        monitorTypeSurfaces: 'include',
      } as DisplayMediaStreamOptions),
    );
    if (!stream) return null;
    const track = stream.getVideoTracks()[0];
    track.contentHint = 'detail';
    const surface = (track.getSettings() as any).displaySurface as string | undefined;
    const name = track.label && !/^screen:|^window:|^web-contents/.test(track.label) ? track.label : '';
    const label =
      surface === 'window' ? `Window${name ? ` – ${name}` : ''}` : surface === 'browser' ? `Tab${name ? ` – ${name}` : ''}` : 'Screen';
    const sound = stream.getAudioTracks()[0];
    const screen = this.add({ kind: 'screen', label: label.slice(0, 50), track, audioTrack: sound, use });
    // The screen's sound (a video, a game, an app) becomes its own WAV, with its own meter and mute, so the
    // editor gets it on a separate track.
    if (sound) {
      const soundLabel = surface === 'browser' ? `Tab sound${name ? ` – ${name}` : ''}` : surface === 'window' ? 'Window sound' : 'Computer sound';
      this.add({ kind: 'system-audio', label: soundLabel.slice(0, 50), track: sound, use: { ...use }, parentId: screen.id });
    } else {
      console.info(`[takehouse] ${label}: shared without sound`);
    }
    this.soundNote = sound ? null : noSoundNote(surface);
    this.changed();
    return screen;
  }

  dismissSoundNote() {
    this.soundNote = null;
    this.changed();
  }

  /** The sound captured with a screen share, if any. */
  soundOf(screen: CapturedSource | undefined) {
    return screen ? this.active.find((s) => s.kind === 'system-audio' && s.parentId === screen.id) : undefined;
  }

  private async capture(what: string, fn: () => Promise<MediaStream>) {
    try {
      return await fn();
    } catch (e) {
      const err = e as DOMException;
      console.warn(`[takehouse] couldn't open ${what}: ${err.name}: ${err.message}`);
      if (err.name === 'NotAllowedError') this.error = 'Permission was denied. Allow access in the browser’s address bar and try again.';
      else if (err.name === 'NotFoundError') this.error = 'No such device was found.';
      else if (err.name === 'NotReadableError') this.error = 'The device is busy – another app may be using it.';
      else if (err.name === 'AbortError') this.error = null;
      else this.error = err.message || String(e);
      this.changed();
      return null;
    }
  }

  remove(id: string) {
    const s = this.byId(id);
    if (!s) return;
    console.info(`[takehouse] source removed: ${s.label}`);
    s.track.stop();
    this.markEnded(s);
  }

  /** Swap a camera or mic for another device. Recording treats it as one source ending and another starting. */
  async switchDevice(id: string, deviceId: string) {
    const s = this.byId(id);
    if (!s || (s.kind !== 'camera' && s.kind !== 'microphone')) return;
    const next = s.kind === 'camera' ? await this.addCamera(deviceId, s.use) : await this.addMicrophone(deviceId, s.use);
    if (next) {
      next.use = s.use;
      next.label = s.label;
      this.remove(id);
    }
  }

  setUse(id: string, use: Partial<SourceUse>) {
    const s = this.byId(id);
    if (!s) return;
    s.use = { ...s.use, ...use };
    // Only one camera and one mic represent "me" live; promoting one demotes the other.
    if (use.live && (s.kind === 'camera' || s.kind === 'microphone')) {
      for (const o of this.active) if (o !== s && o.kind === s.kind) o.use = { ...o.use, live: false };
    }
    this.sources = [...this.sources];
    this.changed();
    if (s.kind === 'camera' || s.kind === 'screen') void this.applyPlan();
  }

  setLiveMuted(id: string, muted: boolean) {
    const s = this.byId(id);
    if (!s) return;
    s.liveMuted = muted;
    this.sources = [...this.sources];
    this.changed();
  }

  setHardMuted(id: string, muted: boolean) {
    const s = this.byId(id);
    if (!s) return;
    s.hardMuted = muted;
    this.sources = [...this.sources];
    this.changed();
  }

  rename(id: string, label: string) {
    const s = this.byId(id);
    if (!s) return;
    s.label = label.slice(0, 50) || s.label;
    this.sources = [...this.sources];
    this.changed();
  }

  async setProcessing(p: AudioProcessing) {
    this.processing = p;
    savePrefs({ processing: p });
    const voice = p === 'voice';
    for (const s of this.active.filter((s) => s.kind === 'microphone')) {
      await s.track
        .applyConstraints({ echoCancellation: voice, noiseSuppression: voice, autoGainControl: voice })
        .catch(() => {});
    }
    this.changed();
  }

  stopAll() {
    for (const s of this.sources) {
      s.track.stop();
      s.audioTrack?.stop();
    }
    this.sources = [];
    this.changed();
  }
}

export const muteState = (s: CapturedSource): 'none' | 'send' | 'hard' => (s.hardMuted ? 'hard' : s.liveMuted ? 'send' : 'none');

export function describeTrack(s: CapturedSource) {
  const st = s.track.getSettings();
  if (s.kind === 'microphone' || s.kind === 'system-audio') {
    return [st.sampleRate ? `${st.sampleRate / 1000} kHz` : '', st.channelCount ? `${st.channelCount} ch` : '']
      .filter(Boolean)
      .join(' · ');
  }
  return `${st.width ?? '?'}×${st.height ?? '?'}${st.frameRate ? ` · ${Math.round(st.frameRate)} fps` : ''}${s.audioTrack ? ' · audio' : ''}`;
}

/**
 * The rate a microphone really delivers. Bluetooth headsets (AirPods and the like) drop to 16 kHz "call
 * quality" while their mic is in use, and some browsers still report 48 kHz in the track's settings, so where
 * the page can read the audio itself (Chrome, Edge) one block is read from a copy of the track. Elsewhere the
 * settings are all there is.
 */
export async function measureSampleRate(track: MediaStreamTrack): Promise<number | null> {
  const reported = track.getSettings().sampleRate ?? null;
  const Processor = (window as any).MediaStreamTrackProcessor;
  if (!Processor || pageProcessorInstalled()) return reported; // ours reads through a 48 kHz AudioContext
  const copy = track.clone();
  try {
    const reader = new Processor({ track: copy }).readable.getReader();
    const read = reader.read().then((r: ReadableStreamReadResult<AudioData>) => r.value);
    const block: AudioData | undefined = await Promise.race([read, new Promise<undefined>((r) => setTimeout(r, 2000))]);
    const rate = block?.sampleRate ?? null;
    block?.close();
    void reader.cancel().catch(() => {});
    return rate ?? reported;
  } catch {
    return reported;
  } finally {
    copy.stop();
  }
}

/** Below this a microphone is running at call quality rather than recording quality. */
export const CALL_QUALITY_RATE = 32000;

/** Why a screen share came without sound, and what to do about it. */
function noSoundNote(surface: string | undefined) {
  const ua = navigator.userAgent;
  const browser = /firefox/i.test(ua) ? 'Firefox' : /safari/i.test(ua) && !/chrome|chromium|edg/i.test(ua) ? 'Safari' : null;
  if (browser) {
    return `${browser} can’t share a screen’s sound. To record an app’s or game’s sound, use Chrome or Edge, or add a virtual audio device such as BlackHole as a microphone.`;
  }
  if (surface === 'window') return 'A single window shares no sound. For its sound, share your Entire Screen and switch on “Also share system audio”.';
  if (surface === 'browser') return 'Shared without the tab’s sound. To include it, share again and switch on the tab audio option in the picker.';
  return 'Shared without the computer’s sound. To include it, share again and switch on “Also share system audio” at the bottom of the picker.';
}
